import { useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Description,
  Image as ImageIcon,
  InsertDriveFile,
  MoreHoriz,
  PictureAsPdf,
  Slideshow,
  TableChart,
  FolderZip,
  Movie,
  AudioFile,
} from '@mui/icons-material'

import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import AuthImage from './AuthImage'
import PhotoViewer from './PhotoViewer'
import { NewBadge } from './MessageContent'
import { subscribeAttachmentOpened } from '../../utils/attachmentEvents'
import { formatSize } from './MessageContent'

const fileIconOf = (name = '', type = '') => {
  const ext = name.split('.').pop().toLowerCase()
  if (type === 'application/pdf' || ext === 'pdf') return PictureAsPdf
  if (['doc', 'docx', 'txt', 'rtf', 'md'].includes(ext)) return Description
  if (['xls', 'xlsx', 'csv'].includes(ext)) return TableChart
  if (['ppt', 'pptx'].includes(ext)) return Slideshow
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return FolderZip
  if (type.startsWith('video/')) return Movie
  if (type.startsWith('audio/')) return AudioFile
  return InsertDriveFile
}

const formatDate = (d) =>
  new Date(d).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })

// Teams-style "Files" / "Photos" tab for one chat.
// kind: 'files' | 'photos'; params: { recipientId } or { groupId }
const ChatAttachments = ({ kind, params, userById, meId, onOpen, onOpenMenu, onDownload, onMarkOpened }) => {
  const theme = useTheme()
  const api = useAxiosPrivate()
  const accent = theme.palette.secondary.main

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(null)   // photo shown full screen

  const paramKey = params.groupId || params.recipientId

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setFailed(false)

    api
      .get('/api/messages/attachments', { params: { ...params, kind }, signal: controller.signal })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') setFailed(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, kind, paramKey])

  // Opened anywhere (this tab, another tab, another device): its "New" badge goes away
  useEffect(
    () =>
      subscribeAttachmentOpened((id) =>
        setItems((prev) => (prev.some((m) => m._id === id && m.isNew) ? prev.map((m) => (m._id === id ? { ...m, isNew: false } : m)) : prev))
      ),
    []
  )

  // A photo shown in the viewer counts as opened
  useEffect(() => {
    const m = viewerIndex !== null ? items[viewerIndex] : null
    if (m?.isNew) onMarkOpened?.(m)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerIndex])

  const senderName = (id) => (id === meId ? 'You' : userById[id]?.name || 'Someone')

  if (loading) {
    return (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (failed || items.length === 0) {
    const Icon = kind === 'photos' ? ImageIcon : InsertDriveFile
    return (
      <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1} p={3}>
        <Icon sx={{ fontSize: 48, color: alpha(accent, 0.6) }} />
        <Typography color="text.secondary">
          {failed
            ? `Unable to load ${kind}.`
            : kind === 'photos'
            ? 'No photos have been shared in this chat yet.'
            : 'No files have been shared in this chat yet.'}
        </Typography>
      </Box>
    )
  }

  const scrollSx = {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18) transparent' : 'rgba(0,0,0,0.18) transparent',
  }

  if (kind === 'photos') {
    return (
      <Box sx={{ ...scrollSx, p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 1,
          }}
        >
          {items.map((m, i) => (
            <Tooltip key={m._id} title={`${m.attachment.name} · ${senderName(m.senderId)} · ${formatDate(m.createdAt)}`}>
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: alpha(accent, 0.06),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover .photo-more': { opacity: 1 },
                  '&:hover img': { transform: 'scale(1.04)' },
                }}
                onClick={() => setViewerIndex(i)}
              >
                <AuthImage
                  src={`${process.env.REACT_APP_SERVER_URL}${m.attachment.url}`}
                  alt={m.attachment.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s ease' }}
                />
                {m.isNew && <NewBadge sx={{ position: 'absolute', top: 6, left: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />}
                <IconButton
                  className="photo-more"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onOpenMenu(e, m) }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    opacity: 0,
                    transition: 'opacity .2s',
                    color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
                  }}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          ))}
        </Box>

        <PhotoViewer
          items={items}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          onDownload={onDownload}
          onOpen={onOpen}
          userById={userById}
          meId={meId}
        />
      </Box>
    )
  }

  return (
    <Box sx={scrollSx}>
      {/* Column header, like the Teams Files tab */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: '1fr 160px 120px 40px',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {['Name', 'Shared by', 'Date', ''].map((h) => (
          <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
            {h}
          </Typography>
        ))}
      </Box>

      <List disablePadding>
        {items.map((m) => {
          const Icon = fileIconOf(m.attachment.name, m.attachment.type || '')
          const size = formatSize(m.attachment.size)

          return (
            <ListItemButton
              key={m._id}
              onClick={() => onOpen(m)}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 40px', sm: '1fr 160px 120px 40px' },
                gap: 2,
                px: 2,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:hover': { backgroundColor: alpha(accent, 0.08) },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                <Icon sx={{ color: accent, flexShrink: 0 }} />
                <Box minWidth={0}>
                  <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: m.isNew ? 800 : 600 }}>
                      {m.attachment.name}
                    </Typography>
                    {m.isNew && <NewBadge />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {size}
                    {/* On phones the "Shared by" and "Date" columns are hidden, so show them here */}
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      {size ? ' · ' : ''}{senderName(m.senderId)} · {formatDate(m.createdAt)}
                    </Box>
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" noWrap color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {senderName(m.senderId)}
              </Typography>
              <Typography variant="body2" noWrap color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {formatDate(m.createdAt)}
              </Typography>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenMenu(e, m) }} aria-label="File options">
                <MoreHoriz fontSize="small" />
              </IconButton>
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )
}

export default ChatAttachments
